// A per-widget column picker: a checkbox list in a popover, hosted in a table's
// OWN header rather than in the page chrome.
//
// Purely presentational — it knows nothing about metrics, search terms or which
// table it is serving. That is the point: the two Google tables have genuinely
// DIFFERENT column vocabularies (one is the shared metric catalogue minus the
// impression-share family, the other is the search-terms row's own scalar
// fields — see tableWidgets.ts), and unifying those would be wrong. Unifying the
// popover that renders them is just not writing the same dropdown twice.

import { useRef, useState } from 'react';
import { Columns3, Table2 } from 'lucide-react';
import { useClickOutside } from '../../lib/useClickOutside';
import { GOOGLE_TABLES, type GoogleTableKey } from './tableWidgets';

export interface ColumnOption {
  key: string;
  label: string;
  hint?: string;
}

export function ColumnsMenu({
  options,
  selected,
  onChange,
  title,
}: {
  options: readonly ColumnOption[];
  selected: readonly string[];
  /** Emits in OPTIONS order, so the caller never has to re-sort. */
  onChange: (next: string[]) => void;
  /** Tooltip — names the table, since a page can host several of these. */
  title: string;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useClickOutside([ref], () => setOpen(false), open);

  const toggle = (key: string): void => {
    const next = new Set(selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onChange(options.filter((o) => next.has(o.key)).map((o) => o.key));
  };

  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        title={title}
        className="inline-flex h-7 items-center gap-1.5 rounded-md border border-input bg-background px-2 text-xs hover:bg-accent"
      >
        <Columns3 className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
        <span className="text-muted-foreground">Columns</span>
        <span className="font-medium">{selected.length}</span>
      </button>
      {open && (
        <div
          role="menu"
          // Scrolls itself: the campaign-type table's list is the whole metric
          // catalogue, which is longer than a popover should ever grow to.
          className="scroll-subtle absolute right-0 z-30 mt-1 max-h-80 w-64 overflow-y-auto rounded-md border border-border bg-popover p-2 text-sm shadow-lg"
        >
          {options.map((o) => (
            <label
              key={o.key}
              className="flex cursor-pointer items-start gap-2 rounded px-1.5 py-1.5 hover:bg-accent"
            >
              <input
                type="checkbox"
                checked={selected.includes(o.key)}
                onChange={() => toggle(o.key)}
                className="mt-0.5"
              />
              <span>
                <span className="block">{o.label}</span>
                {o.hint && <span className="block text-xs text-muted-foreground">{o.hint}</span>}
              </span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Which of the two secondary TABLES are on the page — the tables' analogue of
 * the charts menu, and deliberately its own small button rather than a second
 * group folded under one labelled "Charts".
 *
 * Hiding both is allowed: the tables region simply renders nothing, exactly as
 * the charts region does when every chart is off.
 */
export function GoogleTablesMenu({
  selected,
  onChange,
}: {
  selected: readonly GoogleTableKey[];
  onChange: (next: GoogleTableKey[]) => void;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useClickOutside([ref], () => setOpen(false), open);

  const toggle = (key: GoogleTableKey): void => {
    const next = new Set(selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onChange(GOOGLE_TABLES.filter((t) => next.has(t.key)).map((t) => t.key));
  };

  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        title="Choose which tables to show"
        className="inline-flex h-8 items-center gap-1.5 rounded-md border border-input bg-background px-2 text-sm hover:bg-accent"
      >
        <Table2 className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
        <span className="text-xs text-muted-foreground">Tables</span>
        <span className="font-medium">{selected.length}</span>
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 z-30 mt-1 w-64 rounded-md border border-border bg-popover p-2 text-sm shadow-lg"
        >
          {GOOGLE_TABLES.map((t) => (
            <label
              key={t.key}
              className="flex cursor-pointer items-start gap-2 rounded px-1.5 py-1.5 hover:bg-accent"
            >
              <input
                type="checkbox"
                checked={selected.includes(t.key)}
                onChange={() => toggle(t.key)}
                className="mt-0.5"
              />
              <span>
                <span className="block">{t.label}</span>
                <span className="block text-xs text-muted-foreground">{t.hint}</span>
              </span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
